/* Pure UI helpers for the Help Center (Figma CrewKnit spec, dc.html:6845).
   Static Figma categories with dynamic backend mapping. No API calls, no hooks. */

export type HelpStaticKey = "leave-attendance" | "timesheet" | "payroll" | "account";

export interface HelpStaticCategory {
  key: HelpStaticKey;
  label: string;
  /** Lucide icon name rendered by HelpCategories */
  icon: "CalendarClock" | "Clock" | "Wallet" | "UserCog";
  /** Token classes for the 46px icon tile (Figma tile colors) */
  tileBg: string;
  tileColor: string;
  /** Backend module codes (uppercased) that belong to this card */
  codes: string[];
}

export const HELP_STATIC_CATEGORIES: HelpStaticCategory[] = [
  {
    key: "leave-attendance",
    label: "Leave & Attendance",
    icon: "CalendarClock",
    tileBg: "bg-[var(--color-primary-100)]",
    tileColor: "text-[var(--color-primary-700)]",
    codes: ["LEAVE", "LEAVE_ATTENDANCE", "ATTENDANCE", "COMP_OFF", "COMPOFF", "TIME_OFF", "TIMEOFF", "REGULARIZATION", "CHECKIN", "CHECK_IN", "PUNCH"],
  },
  {
    key: "timesheet",
    label: "Timesheet",
    icon: "Clock",
    tileBg: "bg-[var(--color-secondary-100)]",
    tileColor: "text-[var(--color-secondary-700)]",
    codes: ["TIMESHEET", "TIME_SHEET"],
  },
  {
    key: "payroll",
    label: "Payroll & Benefits",
    icon: "Wallet",
    tileBg: "bg-[var(--color-warning-light)]",
    tileColor: "text-[var(--color-warning-dark)]",
    codes: ["PAYROLL", "BILLING", "LICENSE", "BILLING_LICENSE", "SALARY", "PAYSLIP", "BENEFITS"],
  },
  {
    key: "account",
    label: "Account & Profile",
    icon: "UserCog",
    tileBg: "bg-[var(--color-info-light)]",
    tileColor: "text-[var(--color-info)]",
    codes: [],
  },
];

/** Map any backend moduleCode to its static card (fallback: Account & Profile).
 *  Display-only: backend codes are matched as they arrive; nothing is
 *  rewritten, so every role sees the same real data, only grouped for display. */
export function mapModuleToCategory(moduleCode: string): HelpStaticCategory {
  const code = (moduleCode || "").toUpperCase().trim();
  // 1. Exact match against the known backend codes
  const exact = HELP_STATIC_CATEGORIES.find(
    (c) => c.key !== "account" && c.codes.includes(code)
  );
  if (exact) return exact;
  // 2. Keyword fallback for future backend codes (timesheet checked first
  //    so time-tracking codes never get swallowed by the leave group)
  if (/TIMESHEET|TIME_SHEET/.test(code)) return HELP_STATIC_CATEGORIES[1];
  if (/LEAVE|ATTENDANCE|REGULARIZ|CHECKIN|CHECK_IN|PUNCH|COMP_?OFF|TIME_?OFF/.test(code))
    return HELP_STATIC_CATEGORIES[0];
  if (/PAYROLL|BILLING|LICENSE|SALARY|PAYSLIP|BENEFIT/.test(code)) return HELP_STATIC_CATEGORIES[2];
  return HELP_STATIC_CATEGORIES[3];
}

/** Estimated read time label from raw article content (~200 wpm, min 1 min). */
export function getReadTime(content: string): string {
  const words = (content || "").replace(/[#*`>\-\d.]/g, "").split(/\s+/).filter(Boolean).length;
  const mins = Math.max(1, Math.ceil(words / 200));
  return `${mins} min read`;
}

export interface FeedbackDisplay {
  /** Parsed "Reasons: a, b" lead (reader flow) — empty for free-text feedback */
  reasons: string[];
  /** Remaining note text (or the full description for free-text feedback) */
  note: string;
}

/**
 * Split feedback descriptions into reason pills + note.
 * Reader flow sends "Reasons: a, b | Note: text"; contact-form flow is free text.
 */
export function getFeedbackDisplay(description: string): FeedbackDisplay {
  const text = description || "";
  const marker = "Reasons:";
  if (!text.includes(marker)) return { reasons: [], note: text };
  const after = text.slice(text.indexOf(marker) + marker.length);
  const noteSep = "| Note:";
  const sepIdx = after.indexOf(noteSep);
  const reasonPart = sepIdx >= 0 ? after.slice(0, sepIdx) : after;
  const note = sepIdx >= 0 ? after.slice(sepIdx + noteSep.length).trim() : "";
  const reasons = reasonPart
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
  return { reasons, note };
}
